import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { IConversation } from 'src/api/landlord-messages-api';
import { getBestChannelForConversation, type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context/types';
import { type ILead } from 'src/services/api';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import { useConversationDrawer, useIsConversationDrawerEnabled } from 'src/pages/landlord/conversations/components/drawer/use-conversation-drawer';

export const getLink = (conversation: IConversation | IConversationMatchWithChannel, lead: ILead, backPath: string, showNavigation: boolean = true) => {
    const path = '/landlord/inner/messages/from-lead';
    const params = new URLSearchParams();

    params.set('lead_uuid', conversation.lead_uuid);

    const channel = getBestChannelForConversation(conversation, lead);
    params.set('channel', channel);

    if (showNavigation) {
        params.set('show_navigation', 'true');
    }

    // base URL is required but will not be used
    const backUrl = new URL(backPath, 'https://platform.reffie.me/');
    // delete back_path to avoid nesting
    backUrl.searchParams.delete('back_path');
    params.set('back_path', `${backUrl.pathname}?${backUrl.searchParams}`);
    return `${path}?${params.toString()}`;
};

export const useOpenConversation = (conversations: (IConversation | IConversationMatchWithChannel)[], leadsByUuid: Map<string, ILead>) => {
    const navigate = useNavigate();
    const isDrawerEnabled = useIsConversationDrawerEnabled();
    const { open: openDrawer } = useConversationDrawer();

    const [, saveConversationsStorage] = usePersistentState(
        'conversations',
        [] as IConversation[],
        (value): value is IConversation[] => Array.isArray(value)
    );

    return useCallback(async (conversation: IConversation | IConversationMatchWithChannel, backPath: string) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead) {
            console.warn('Lead not found for conversation, refusing to open conversation', conversation);
            return;
        }

        const inNavigationList = conversations.some(c => c.lead_uuid === conversation.lead_uuid);
        if (!inNavigationList) {
            console.warn('Conversation not in navigation list, opening without prev/next navigation', conversation);
            if (isDrawerEnabled) {
                const channel = getBestChannelForConversation(conversation, lead);
                openDrawer(conversation.lead_uuid, channel, false);
            } else {
                navigate(getLink(conversation, lead, backPath, false));
            }
            return;
        }

        // Save list for prev/next navigation inside the messages view
        await saveConversationsStorage(conversations as IConversation[]);

        if (isDrawerEnabled) {
            const channel = getBestChannelForConversation(conversation, lead);
            openDrawer(conversation.lead_uuid, channel, true);
            return;
        }

        navigate(getLink(conversation, lead, backPath, true));
    }, [conversations, leadsByUuid, isDrawerEnabled, openDrawer, navigate]);
};